const SUPPRESSED_TEXT = 'NO_RECOMMENDATIONS';

const queryShadowAll = (selector: string, root: ParentNode = document): Element[] => {
  const results: Element[] = [];

  const traverse = (node: ParentNode): void => {
    node.querySelectorAll('*').forEach(el => {
      if (el.matches(selector)) results.push(el);
      if (el.shadowRoot) traverse(el.shadowRoot);
    });
  };

  traverse(root);

  return results;
};

const hideNoRecommendationsMessages = (root: ParentNode = document): void => {
  const walk = (node: ParentNode): void => {
    node.querySelectorAll('*').forEach(el => {
      if (el instanceof HTMLElement
        && !el.dataset.cxAgentNoRecsHidden
        && el.textContent?.trim() === SUPPRESSED_TEXT
        && !el.querySelector('*')) {
        const bubble = el.closest('[class*="message"], [class*="bubble"]') as HTMLElement | null;
        const target = bubble ?? el;
        target.style.display = 'none';
        target.dataset.cxAgentNoRecsHidden = '1';
      }
      if (el.shadowRoot) walk(el.shadowRoot);
    });
  };

  walk(root);
};

/**
 * Hides any chat message bubble whose text equals `NO_RECOMMENDATIONS`, so the
 * agent's empty-result sentinel never reaches the UI. Watches the messenger's
 * message list (and shadow DOM) for added/changed nodes and returns a teardown
 * that detaches the observer and listener.
 */
export const setupNoRecommendationsSuppressor = (): () => void => {
  let observer: MutationObserver | null = null;
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  const scheduleSuppress = (): void => {
    if (timeoutId !== null) clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      timeoutId = null;
      hideNoRecommendationsMessages();
    }, 50);
  };

  const onMessageListLoaded = (): void => {
    const [ msgList ] = queryShadowAll('chat-messenger-message-list');
    if (!msgList?.shadowRoot) return;
    scheduleSuppress();
    observer?.disconnect();
    observer = new MutationObserver(scheduleSuppress);
    observer.observe(msgList.shadowRoot, { childList: true, subtree: true, characterData: true });
  };

  window.addEventListener('chat-messenger-message-list-loaded', onMessageListLoaded);

  return (): void => {
    window.removeEventListener('chat-messenger-message-list-loaded', onMessageListLoaded);
    observer?.disconnect();
    observer = null;
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };
};
